import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { CompanyDomain, SsoIntegrationWarningKeyEnum } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import CircleLoaderWrapper from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import ActionsMenu from '@lib/components/ActionsMenu/ActionsMenu';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Table from '@lib/components/Table/Table';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Trans from '@lib/components/Trans/Trans';
import Typography from '@lib/components/Typography/Typography';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import { materialSymbolsOutlined } from '@lib/enums/common';
import { IconName } from '@lib/components/Modal/enums';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';

import VerifyDomainModal from '../CompanyDomains/components/VerifyDomainModal/VerifyDomainModal';
import SsoDetailsSection from './components/SsoDetailsSection';
import SsoConfigurationSection from './components/SsoConfigurationSection';
import SpViewConfigurationSection from './components/SpViewConfigurationSection';
import SsoWarningBanner from './components/SsoWarningBanner';
import getSsoViewPageActionsMenu from './utils/getSsoViewPageActionsMenu';
import getSsoPresetLabel from './utils/getSsoPresetLabel';
import getSsoIconSrc from './utils/getSsoIconSrc';
import useSsoIntegrationViewActions from './utils/useSsoIntegrationViewActions';
import useSupportedMappingFieldsOptions from './utils/useSupportedMappingFieldsOptions';
import getDomainTableColumns from '../CompanyDomains/utils/getTableColumns';
import getDomainTableActionsMenu from '../CompanyDomains/utils/getTableActionsMenu';
import styles from './SsoIntegrationView.module.scss';

const modals = [
  Modals.SsoIntegrationDestroyModal,
  Modals.CompanyDomainDestroyModal,
  Modals.CompanyDomainVerifyModal,
];

export default function SsoIntegrationView() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { id = '' } = useParams();

  const useModalManagementResult = useModalManagement(modals);
  const { closeModal, isModalOpened } = useModalManagementResult;

  const { supportedMappingFields } = useSupportedMappingFieldsOptions();

  const {
    ssoIntegrationQueryHookResult,
    companyDomainsQueryHookResult,
    paginationState,
    orderingState,
    isDomainUsedBySso,
    selectedDomain,
    verifyDomainMutationResult,
    onEdit,
    onActivate,
    onDeactivate,
    onDelete,
    onConfirmedDelete,
    onVerifyDomain,
    onConfirmedVerifyDomain,
    onDeleteDomain,
    onConfirmedDeleteDomain,
  } = useSsoIntegrationViewActions({ id, useModalManagementResult });

  const { data, loading, firstLoading } = ssoIntegrationQueryHookResult;
  const isFirstLoading =
    firstLoading || companyDomainsQueryHookResult.firstLoading;

  // Derived data
  const providerName =
    data?.name || (data ? getSsoPresetLabel(data.providerPreset, t) : '');
  const iconSrc = data ? getSsoIconSrc(data) : '';
  const domains = companyDomainsQueryHookResult.collection;

  const breadCrumbsData = useMemo(
    () => getBreadCrumbsDataByKey({ pathname, t, entity: data }),
    [pathname, t, data],
  );

  const actionsMenu = getSsoViewPageActionsMenu({
    t,
    onActivate,
    onDeactivate,
    onDelete,
    status: data?.status,
  });

  const domainTableColumns = getDomainTableColumns({ t });
  const domainTableActionsMenu = getDomainTableActionsMenu({
    t,
    onVerify: onVerifyDomain,
    onDelete: onDeleteDomain,
  });

  const title = (
    <div className={styles.titleRow}>
      {iconSrc && (
        <img src={iconSrc} alt={providerName} className={styles.titleIcon} />
      )}
      <span>{providerName}</span>
    </div>
  );

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <PageBody withoutHeader>
        <DashboardContentMaxWidth>
          <CircleLoaderWrapper isLoading={isFirstLoading}>
            <WhiteBox>
              <DashboardTitleHeader
                title={title}
                actions={[
                  <Button
                    key="edit"
                    buttonText={t('edit')}
                    buttonType={ButtonTypes.primaryFilled}
                    onClick={onEdit}
                    leftIcon="edit"
                    leftIconClassName={materialSymbolsOutlined}
                    disabled={loading}
                  />,
                  <ActionsMenu
                    key="actions-menu"
                    actions={actionsMenu}
                    disabled={loading}
                  />,
                ]}
              />

              <SsoDetailsSection data={data} />

              {/* Domains section */}
              <div className={styles.section}>
                <Typography variant="h3" className={styles.sectionHeader}>
                  {t('domains')}
                </Typography>
                <SsoWarningBanner
                  warningKey={
                    data?.warningKey as
                      | SsoIntegrationWarningKeyEnum
                      | null
                      | undefined
                  }
                  onEdit={onEdit}
                />
                {isFirstLoading || !domains || domains.length === 0 ? null : (
                  <Table<CompanyDomain>
                    actionsMenu={domainTableActionsMenu}
                    columns={domainTableColumns}
                    data={domains}
                    id="sso-view-domains"
                    loading={companyDomainsQueryHookResult.loading}
                    orderingState={orderingState}
                    paginationState={paginationState}
                    totalEntries={companyDomainsQueryHookResult.totalCount}
                  />
                )}
              </div>

              <SsoConfigurationSection
                data={data}
                supportedMappingFields={supportedMappingFields}
              />
              <SpViewConfigurationSection code={data?.code} />
            </WhiteBox>
            <div className={entityViewStyles.actions}>
              <Button
                buttonText={t('delete')}
                buttonType={ButtonTypes.negativeOutlined}
                leftIcon="delete"
                leftIconClassName={materialSymbolsOutlined}
                onClick={onDelete}
              />
            </div>
          </CircleLoaderWrapper>
        </DashboardContentMaxWidth>
      </PageBody>

      {/* Modals */}
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-sso-connection-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.SsoIntegrationDestroyModal)}
        onClose={() => closeModal(Modals.SsoIntegrationDestroyModal)}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-domain-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.CompanyDomainDestroyModal)}
        onClose={() => closeModal(Modals.CompanyDomainDestroyModal)}
        onConfirm={onConfirmedDeleteDomain}
        title={t('are-you-sure')}
        modalContentClassName={styles.modalContentClassName}
        useProgress
      >
        {isDomainUsedBySso ? (
          <SupportiveMessage
            color={SupportiveMessageColor.Yellow}
            type={SupportiveMessageType.FilledWithLeftBorder}
            text={t('domain-used-by-sso-warning')}
          />
        ) : undefined}
      </ConfirmationModal>
      <VerifyDomainModal
        dnsTxtRecord={selectedDomain?.dnsTxtRecord || ''}
        isOpen={isModalOpened(Modals.CompanyDomainVerifyModal)}
        loading={verifyDomainMutationResult.loading}
        onClose={() => closeModal(Modals.CompanyDomainVerifyModal)}
        onVerify={onConfirmedVerifyDomain}
      />
    </>
  );
}
